// Canned, client-side welcome greetings shown when the user picks an agent on
// the WelcomeScreen or is redirected to another agent mid-conversation. Texts
// must match what the backend agents expect as conversational openers.

export interface GreetingPair {
  hello: string;
  intro: string;
}

const AGENT_INTROS: Record<string, { tr: string; en: string }> = {
  'mental-health': {
    tr: 'Bugün nasıl hissediyorsun? Duyguların, düşüncelerin veya yaşadıkların üzerine konuşmak istersen buradayım.',
    en: "How are you feeling today? I'm here if you'd like to talk about your emotions, thoughts, or what you're going through.",
  },
  'nutrition': {
    tr: 'Sağlıklı beslenme yolculuğunda yanındayım. Beslenme planı oluşturmak, hedef belirlemek, kalori ve kilo takibi yapmak, tarif önerileri almak veya beslenme alışkanlıkların hakkında konuşmak istersen, buradayım.',
    en: "I'm here to support your healthy eating journey. Whether you want to build a nutrition plan, set goals, track calories and weight, get recipe ideas, or talk about your eating habits — I'm here.",
  },
  'faq': {
    tr: 'Teknik bir sorun mu yaşıyorsun, bir özelliği mi merak ediyorsun, hesabınla ilgili bir sorun mu var, Meditopia\'yı keşfetmek mi istiyorsun? Sana yardımcı olmak için buradayım.',
    en: "Having a technical issue, curious about a feature, dealing with an account problem, or wanting to explore Meditopia? I'm here to help.",
  },
  'content-recommendation': {
    tr: 'Sana uygun meditasyon, müzik, hikâye, yoga veya başka içerikleri birlikte bulalım. Aradığın türü ya da ruh halini paylaşırsan, sana en uygun önerileri getirebilirim.',
    en: "Let's find the right content for you — meditations, music, stories, yoga or more. Share what you're looking for or how you're feeling, and I'll bring the best matches.",
  },
};

// Slightly different FAQ copy used by the mid-conversation redirect button
// (kept verbatim from the original inline implementation).
const REDIRECT_INTROS: Record<string, { tr: string; en: string }> = {
  ...AGENT_INTROS,
  faq: {
    tr: 'Meditopia üyeliği, ödeme, hesap veya teknik konularda nasıl yardımcı olabilirim?',
    en: 'How can I help you with Meditopia membership, billing, account, or technical questions?',
  },
};

const buildHello = (firstName: string, lang: string): string => {
  const isTr = lang === 'tr';
  return isTr
    ? (firstName ? `Merhaba ${firstName}!` : 'Merhaba!')
    : (firstName ? `Hi ${firstName}!` : 'Hi!');
};

/** Long-form greeting: "Merhaba X!" + agent-specific intro paragraph. */
export const buildAgentGreeting = (agentId: string, firstName: string, lang: string): GreetingPair => {
  const intro = AGENT_INTROS[agentId];
  return {
    hello: buildHello(firstName, lang),
    intro: intro ? (lang === 'tr' ? intro.tr : intro.en) : '',
  };
};

/** Greeting used by mid-conversation agent redirect buttons. */
export const buildRedirectGreeting = (agentId: string, firstName: string, lang: string): GreetingPair => {
  const intro = REDIRECT_INTROS[agentId] || REDIRECT_INTROS['mental-health'];
  return {
    hello: buildHello(firstName, lang),
    intro: lang === 'tr' ? intro.tr : intro.en,
  };
};

/** Short-form greeting for returning users (5th+ chat session). */
export const buildShortGreeting = (agentId: string, firstName: string, lang: string): string => {
  const isTr = lang === 'tr';
  const helloShort = isTr
    ? (firstName ? `Merhaba ${firstName}, ` : 'Merhaba, ')
    : (firstName ? `Hi ${firstName}, ` : 'Hi, ');
  const isMh = agentId === 'mental-health';
  return isTr
    ? `${helloShort}${isMh ? 'bugün nasıl hissediyorsun?' : 'sana nasıl yardımcı olabilirim?'}`
    : `${helloShort}${isMh ? 'how are you feeling today?' : 'how can I help you?'}`;
};
